import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/styles';
import {
    Box,
    Button,
    Card,
    CardHeader,
    CardContent,
    CardActions,
    Divider,
    Modal,
    TextField,
    Grid,
    FormControl,
    Badge,
    Link,
    CircularProgress, IconButton, FormControlLabel, Checkbox,
} from '@material-ui/core';
import _ from 'lodash';
import CustomSnackbar from "../../../user/shared/CustomSnackbar";
import useAdminUserSession from "../../../../hooks/useAdminUserSession";
import AdminEditContactContainer from "../../../../containers/admin/AdminEditContactContainer";

import MATransactionService from "../../services/MATransactionService";
import SingleAsyncAutoComplete from "../../shared/SingleAsyncAutoComplete";
import GlobalSearchService from "../../services/GlobalSearchService";
import {KeyboardDatePicker, MuiPickersUtilsProvider} from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import {CurrencyFormatter} from "../../shared/Formatters";
import FilterAutoComplete from "../../shared/FilterAutoComplete";
import useEffectExceptOnMount from "../../../../helpers/useEffectExceptOnMount";
import useSnackbar from "../../../../hooks/useSnackbar";
import MATransactionNews from "./MATransactionNews";
import TriggeringContactsSection from "./TriggeringContactsSection";
import { DeleteOutlined} from "@material-ui/icons";
const PrivateWealthTeamForm = React.lazy(() => import("../../riaPrivateWealthTeams/pwtForm/PrivateWealthTeamForm"));
const InvestorForm = React.lazy(() => import("../../investors/form/InvestorForm"));
import {ChipLabel} from "../../shared/Chips";
import {toTitleCase, toUTCNoon, dateFormat, monthYearFormat, dateUSFormat} from "../../../../helpers/utils";
import useAdminNotes from "../../../../hooks/useAdminNotes";
import useInvestorExtraData from "../../investors/hooks/useInvestorExtraData";
import NotesDialog from "../../shared/Dialogs/NotesDialog";
import ConfirmDialog from "../../shared/Dialogs/ConfirmDialog";
import RegenerateBio from "../../shared/Buttons/RegenerateBio";
import MaSourceChips from "../../shared/Chips/MaSourceChips";

const useStyles = makeStyles((theme) => ({
  root: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    outline: 'none',
    boxShadow: theme.shadows[20],
    maxHeight: '100%',
    overflowY: 'auto',
    width: '80%',
    height: '80%',
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  actions: {
    justifyContent: 'flex-end'
  },
  cardContent: {
    display: 'flex',
    flexDirection: 'column',
  },
  sectionTitle: {
    margin: '20px 0',
    fontSize: '20px',
  },
  tableHeader: {
    fontWeight: '600',
    width: 'max-content',
  },
  linkStyle: {
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'none',
      cursor: 'pointer',
    },
  },
  requiredFieldWarning: {
    color: '#f44336',
    fontSize: '0.75rem',
    marginTop: '4px',
    marginLeft: '14px',
  },
  // Status chip styles (moved from MATransaction.jsx)
  statusChip: {
    fontWeight: 600,
    fontSize: 13,
    borderRadius: 16,
    height: 24,
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    color: "#fff",
    whiteSpace: "nowrap",
  },
  draft: {
    backgroundColor: "#800080",
    color: "#fff",
  },
  rejected: {
    backgroundColor: "#d32f2f",
  },
  pending: {
    backgroundColor: "#fbc02d",
    color: "#222",
  },
  approved: {
    backgroundColor: "#388e3c",
  },
  warning: {
    backgroundColor: '#ff9800',
    color: 'white',
  },
}))

const getSelectedOptions = (selected, options) => {
  return selected?.map((selectedId) => options.find(({id}) => id === selectedId) || null).filter((option) => option !== null) || []
}

const MATransactionForm = ({ itemId, onClose, open, onDataUpdate, itemIndex, afterCreate, generalClasses, outsideSnackbar, setSelectedItemId, modalZIndex }) => {
  const classes = useStyles();

  const [validateEnabled, setValidateEnabled] = useState(false)
  const [validateManuallyEnabled, setValidateManuallyEnabled] = useState(false)
  const [saveEnabled, setSaveEnabled] = useState(false)
  const [openRiaContactModal, setOpenRiaContactModal] = useState(false);
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [selectedBankersUsed, setSelectedBankersUsed] = useState([]);
  const [selectedServiceProvidersUsed, setSelectedServiceProvidersUsed] = useState([]);
  const [saving, setSaving] = useState(false)
  const [news, setNews] = useState([]);
  const [newsTypes, setNewsTypes] = useState([]);
  const [riaInvestorParent, setRiaInvestorParent] = useState([]);
  const [fetchingNews, setFetchingNews] = useState(true);
  const [fetchingContacts, setFetchingContacts] = useState(true);
  const [contacts, setContacts] = useState([]);
  const snackbar = useSnackbar();
  const [transactionDateEdited, setTransactionDateEdited] = useState(false);

  // Callback to update transaction when a note is created
  const handleNoteCreated = (transactionData) => {
    if (transactionData && transactionData.updated_at) {
      setTransaction(prev => ({
        ...prev,
        updated_at: transactionData.updated_at
      }));

      // Also update the parent component if needed
      if (onDataUpdate && transactionData.id) {
        onDataUpdate(itemIndex, { updated_at: transactionData.updated_at });
      }
    }
  };

  const adminNotes = useAdminNotes(itemId, "MATransaction", snackbar, MATransactionService.notes, handleNoteCreated);
  const [isFullDate, setIsFullDate] = useState(true);
  const [notesQuantity, setNotesQuantity] = useState(0);
  const [openWealthTeamModal, setOpenWealthTeamModal] = useState(false);
  const [selectedWealthTeamId, setSelectedWealthTeamId] = useState(null);
  const [openFamilyOfficeModal, setOpenFamilyOfficeModal] = useState(false);
  const [selectedFamilyOfficeId, setSelectedFamilyOfficeId] = useState(null);
  const [openConfirmNoDateDialog, setOpenConfirmNoDateDialog] = useState(false);
  const [pendingSubmitEvent, setPendingSubmitEvent] = useState(null);
  const [aumErrors, setAumErrors] = useState({
    manual_last_filed_aum_acquired: '',
    last_filed_aum_acquiring: ''
  });

  const [optionsData, setOptionsData] = useState({
    bankers_used:  [],
    service_providers_used:  [],
  });
  const {session: adminUserSession} = useAdminUserSession();
  const investorExtraData = useInvestorExtraData(adminUserSession);

  const canApproveLive =
    adminUserSession?.isSuperAdmin || adminUserSession?.inHouseResearch;


  const initialTransactionState = {
        source: [],
        acquiring_firm_crd_id: '',
        acquired_firm_crd_id: '',
        last_adv_filing_date: '',
        manual_transaction_date: '',
        transaction_information: '',
        manual_last_filed_aum_acquired: '',
        last_filed_aum_acquiring: '',
        acquired_firm_employee_count: '',
        acquiring_firm_employee_count: '',
        acquired_firm_account_count: '',
        acquiring_firm_account_count: '',
        adv_w_link: '',
        status: '',
        bankers_used: [],
        service_providers_used: [],
        is_full_date: ''
    };

    const [transaction, setTransaction] = useState(initialTransactionState);
    const [originalTransaction, setOriginalTransaction] = useState(initialTransactionState);

    useEffect(() => {
        if (!itemId) return;
        if (adminNotes.open) return;
        const fetchNotesCount = async () => {
            try {
                const notesRes = await MATransactionService.notesCount(itemId, adminUserSession);
                setNotesQuantity(notesRes.data.notes_count || 0);
            } catch (err) {
                console.log("Error fetching notes count:", err.message);
            }
        };

        fetchNotesCount();
    }, [itemId, adminNotes.open]);

    const handleModeToggle = (e) => {
        const checked = e.target.checked;
        setIsFullDate(!checked);
        const newIsFullDate = !checked;

        if (!transaction.manual_transaction_date) {
            setTransaction(prev => ({
                ...prev,
                is_full_date: newIsFullDate,
            }));
            return;
        }

        const dateObj = new Date(transaction.manual_transaction_date);

        if (isNaN(dateObj.getTime())) {
            setTransaction(prev => ({
                ...prev,
                is_full_date: newIsFullDate,
            }));
            return;
        }

        const year = dateObj.getUTCFullYear();
        const month = dateObj.getUTCMonth();
        const day = newIsFullDate ? dateObj.getUTCDate() : 1;

        const updatedDate = toUTCNoon(year, month, day);

        setTransaction(prev => ({
            ...prev,
            is_full_date: newIsFullDate,
            manual_transaction_date: updatedDate,
        }));
    };

    const mapStage = (stage) => {
        if (!stage) return "draft";

        const s = stage.toLowerCase();

        if (s === "draft") return "draft";
        if (s === "pending") return "pending approval";
        if (s === "rejected") return "trash";
        if (s === "approved") return "live";

        return s;
    };

    const fetchTriggeringContacts = async () => {
    setFetchingContacts(true);
    const {data} = await MATransactionService.triggeringContacts(itemId, adminUserSession);
    setContacts(data.contacts);
    setFetchingContacts(false);
  }

    const fetchTransactionData = async (itemId, adminUserSession) => {
        if (itemId !== undefined) {
            try {
                const response = await MATransactionService.edit(itemId, adminUserSession);
                const { transaction: trans, status: responseStatus } = response.data;

                fetchNews();
                fetchTriggeringContacts();

                if (trans.manual_transaction_date) {
                    const [year, month, day] = trans.manual_transaction_date.split('-').map(Number);
                    trans.manual_transaction_date = toUTCNoon(year, month - 1, day);
                }

                setTransaction({ ...trans });
                setOriginalTransaction({ ...trans });

                setIsFullDate(trans.is_full_date);
            } catch (thrown) {
                console.log('error', thrown.message);
            }
        }
    };

    useEffect(() => {
        fetchTransactionData(itemId, adminUserSession);
    }, [itemId]);

  useEffect(() => {
    handleFetchOptions()
  }, [])

    const normalizeTransaction = (obj) => {
        const fieldsToNormalize = [
            'acquiring_firm_crd_id',
            'acquired_firm_crd_id',
            'last_adv_filing_date',
            'manual_transaction_date',
            'transaction_information',
            'manual_last_filed_aum_acquired',
            'last_filed_aum_acquiring',
            'acquired_firm_employee_count',
            'acquiring_firm_employee_count',
            'acquired_firm_account_count',
            'acquiring_firm_account_count',
            'adv_w_link',
            'status',
            'bankers_used',
            'service_providers_used',
            'is_full_date'
        ];

        const normalized = { ...obj };
        fieldsToNormalize.forEach(field => {
            if (normalized[field] === '') normalized[field] = null;
        });
        return normalized;
    };

    const isDirty = React.useMemo(() => {
        const t1 = normalizeTransaction(transaction);
        const t2 = normalizeTransaction(originalTransaction);
        return !_.isEqual(t1, t2);
    }, [transaction, originalTransaction]);

    const handleFetchOptions = (params = {}) => {
    const newParams = {
      page: 1,
      per_page: 50,
      ...params
    };

    MATransactionService.extraOptions(adminUserSession, newParams).then((response) => {
      const { service_providers_used, bankers_used } = response.data;

      setOptionsData({
        service_providers_used: service_providers_used,
        bankers_used: bankers_used,
      })
    });
  }

  useEffect(() => {
    if (!transaction?.manual_transaction_date) return;

    if (!itemId) {
      const allFieldsPresent =
          transaction.acquiring_firm_crd_id &&
          transaction.acquired_firm_crd_id &&
          transaction.manual_transaction_date;

      if (allFieldsPresent) {
        fetchHistoricalFirmData();
      } else {
        clearHistoricalFirmData();
      }
    } else if (transactionDateEdited){
      fetchHistoricalFirmData();
    }

  }, [transaction?.manual_transaction_date, transaction?.acquiring_firm_crd_id, transaction?.acquired_firm_crd_id, itemId]);

  const clearHistoricalFirmData = (type) => {
    setTransaction(prev => {
      const updates = {};

      if (!type || type === 'acquiring') {
        updates.last_filed_aum_acquiring = '';
        updates.acquiring_firm_employee_count = '';
        updates.acquiring_firm_account_count = '';
      }

      if (!type || type === 'acquired') {
        updates.manual_last_filed_aum_acquired = '';
        updates.acquired_firm_employee_count = '';
        updates.acquired_firm_account_count = '';
      }

      return {
        ...prev,
        ...updates
      };
    });
  };

  useEffectExceptOnMount(() => {
    setSelectedServiceProvidersUsed(getSelectedOptions(transaction?.service_providers_used?.map(i=>Number(i)), optionsData.service_providers_used))
    setSelectedBankersUsed(getSelectedOptions(transaction?.bankers_used?.map(i=>Number(i)), optionsData.bankers_used))

  }, [optionsData, transaction])

  const handleOpenContactModal = (contactId) => {
    setSelectedContactId(contactId);
    setOpenRiaContactModal(true);
  }

  const fetchHistoricalFirmData = async () => {
    if (!transaction.acquiring_firm_crd_id || !transaction.acquired_firm_crd_id || !transaction.manual_transaction_date) return;

    try {
      const {data} = await MATransactionService.getHistoricalFirmData(adminUserSession, {
        acquiring_firm_crd_id: transaction.acquiring_firm_crd_id,
        acquired_firm_crd_id: transaction.acquired_firm_crd_id,
        transaction_date: transaction.manual_transaction_date
      });

      setTransaction(prev => ({
        ...prev,
        manual_last_filed_aum_acquired: data?.manual_last_filed_aum_acquired || '',
        last_filed_aum_acquiring: data?.last_filed_aum_acquiring || '',
        acquired_firm_employee_count: data?.acquired_firm_employee_count || '',
        acquiring_firm_employee_count: data?.acquiring_firm_employee_count || '',
        acquired_firm_account_count: data?.acquired_firm_account_count || '',
        acquiring_firm_account_count: data?.acquiring_firm_account_count || ''
      }));
    } catch (error) {
      console.error('Error fetching historical firm data:', error);
      snackbar.show("Error fetching firm data", "error");
    }
  }

  const handleCloseContactModal = () => {
    setSelectedContactId(null);
    setOpenRiaContactModal(false)
  }

    const handleUpdateTransaction = (transaction) => {
        return MATransactionService.update(itemId, adminUserSession, transaction)
            .then((response) => {
                const {
                    status: statusUpdate,
                    message,
                    transaction,
                    transaction_id
                } = response.data;

                if (statusUpdate === 'success') {
                    const updatedTransaction = response.data.transaction;
                    onDataUpdate(updatedTransaction, itemIndex);

                  fetchNews();

                    if (
                        transaction?.status !== 'draft' &&
                        transaction?.status !== 'pending'
                    ) {
                        onClose();
                    } else {
                        fetchTransactionData(itemId, adminUserSession);
                    }
                } else if (statusUpdate === 'refused') {
                    snackbar.show(
                        <span>
                            This firm has already been marked as acquired in transaction{' '}
                            <a
                                href="#"
                                onClick={(e) => {
                                    e.preventDefault();
                                    setSelectedItemId(transaction_id);
                                }}
                                style={{ textDecoration: 'underline', cursor: 'pointer', color: '#007bff' }}
                            >
                              M/A{transaction_id}
                            </a>
                        </span>,
                        "warning"
                    );
                } else {
                    snackbar.show(message, "error");
                    console.log('message', message)
                }
            });
    }

    const handleCreateTransaction = (transaction) => {
        setSaving(true);
        MATransactionService.create(adminUserSession, transaction).then((response) => {
            const {
                data: {
                    status,
                    message,
                    transaction_id
                }
            } = response;
            setSaving(false)
            if (status === 'success') {
                setOriginalTransaction({ ...transaction });
                afterCreate();
            } else if (status === 'refused') {
                snackbar.show(
                    <span>
                        This firm has already been marked as acquired in transaction{' '}
                        <a
                            href="#"
                            onClick={(e) => {
                                e.preventDefault();
                                setSelectedItemId(transaction_id);
                            }}
                            style={{ textDecoration: 'underline', cursor: 'pointer', color: '#007bff' }}
                        >
                            M/A{transaction_id}
                        </a>
                    </span>,
                    "warning"
                );
            } else {
                snackbar.show(message, "error");
                console.log('message', message)
            }
        })
    }

    const handleOnChange = (event) => {
    event.persist();

    const MAX_AUM_VALUE = 9999999999999.99;

    if (['manual_last_filed_aum_acquired', 'last_filed_aum_acquiring'].includes(event.target.name) && event.target.value) {
      // Remove non-numeric characters
      event.target.value = event.target.value.replace(/[^0-9.-]+/g,"");

      // Validate maximum value
      const numericValue = parseFloat(event.target.value);
      if (!isNaN(numericValue) && numericValue > MAX_AUM_VALUE) {
        setAumErrors(prev => ({
          ...prev,
          [event.target.name]: 'Value cannot exceed $9.99 trillion'
        }));
        return;
      } else {
        setAumErrors(prev => ({
          ...prev,
          [event.target.name]: ''
        }));
      }
    }

    setTransaction(prevState => ({
      ...prevState,
      [event.target.name]: event.target.value,
    }));
    setValidateEnabled(true)
  }

  const handleOnChangeInField = (field, value) => {
    setTransaction(prevState => ({
      ...prevState,
      [field]: value,
    }));
    setValidateEnabled(true)
  }

    const handleSubmit = (event) => {
        enableAllButtons(false);
        event.persist();
        event.preventDefault();

        // Check for AUM errors before proceeding
        if (aumErrors.last_filed_aum_acquired || aumErrors.last_filed_aum_acquiring) {
            snackbar.show("Please fix AUM validation errors before submitting", "error");
            enableAllButtons(true);
            return;
        }

        const blankValues = Object.keys(
            _.pickBy(transaction, (item) => (
                [null, undefined, ''].includes(item) || (item.constructor.name === 'Array' && _.isEmpty(item))
            ))
        );

        transaction.is_full_date = isFullDate

        if (!_.isEmpty(_.intersection(blankValues, ['acquiring_firm_crd_id', 'acquired_firm_crd_id']))) {
            let message = {};
            blankValues.forEach((value) => {
                message[value] = "Can't be blank!";
            });
            snackbar.show(message, "error");
            enableAllButtons(true);
        } else {
            if (!transaction.manual_transaction_date) {
                setPendingSubmitEvent(event);
                setOpenConfirmNoDateDialog(true);
                enableAllButtons(true);
                return;
            }

            // Proceed with save
            if (itemId !== undefined) {
                // Restrict saving live records
                if (transaction.status === 'approved' &&
                    !adminUserSession?.isSuperAdmin &&
                    !adminUserSession?.inHouseResearch) {
                    snackbar.show("You do not have permission to update a live transaction", "error");
                    enableAllButtons(true);
                    return;
                }

                handleUpdateTransaction(transaction);
            } else {
                // For create, just allow it
                handleCreateTransaction(transaction);
            }
        }
    };

    const handleCloseSnackBar = () => {
    snackbar.close()
  }

  const investors = (query) => {
    return GlobalSearchService.ria_investors(query, adminUserSession)
  }

  const enableAllButtons = (val=true) => {
    setValidateEnabled(val)
    setSaveEnabled(val)
    setValidateManuallyEnabled(val)
  }

  const handleBankersUsedChange = (bankersUsed) => {
    setSelectedBankersUsed(bankersUsed)
    handleOnChangeInField('bankers_used', bankersUsed.map(({id}) => id))
  }

  const handleServiceProvidersUsedChange = (serviceProviders) => {
    setSelectedServiceProvidersUsed(serviceProviders)
    handleOnChangeInField('service_providers_used', serviceProviders.map(({id}) => id))
  }

  const fetchNews = async () => {
    setFetchingNews(true);
    const {data: {news, news_types, ria_investors}} = await MATransactionService.news(itemId, adminUserSession);
    setNews(news);
    setNewsTypes(news_types);
    setRiaInvestorParent(ria_investors);
    setFetchingNews(false);
  }

  const handleReject = async () => {
    setSaving(true)
    if (window.confirm(`Are you sure you want to move this record to the trash?`)) {
      try {
        const rejectResponse = await MATransactionService.reject(transaction.id, adminUserSession);
        if (rejectResponse.data.status === 'success') {
          setSaving(false)
          outsideSnackbar.show("Moved to the trash", "success");
          setTransaction(prevState => ({
            ...prevState,
            status: 'rejected'
          }))
          onDataUpdate(transaction, itemIndex);
          onClose();
        } else {
          setSaving(false)
          snackbar.show(rejectResponse.data.message, "warning");

        }
      } catch (e) {
        setSaving(false)
        console.error(e);
        snackbar.show("Error when rejecting Transaction", "warning");
      }
    } else {
        setSaving(false);
    }
  }

    const handleMoveToDraft = async () => {
        try {
            setSaving(true);
            const draftResponse = await MATransactionService.moveToDraft(transaction?.id, adminUserSession);

            const { status, message, transaction_id } = draftResponse?.data;

            if (status === 'success') {
                outsideSnackbar.show("Moved to the draft stage", "success");
                setTransaction(prevState => ({
                    ...prevState,
                    status: 'draft'
                }));
                onDataUpdate(transaction, itemIndex);
                onClose();

            } else if (status === 'refused') {
                snackbar.show(
                    <span>
                        This firm has already been marked as acquired in transaction{' '}
                        <a href="#"
                            onClick={(e) => {
                                e.preventDefault();
                                setSelectedItemId(transaction_id);
                            }}
                            style={{ textDecoration: 'underline', cursor: 'pointer', color: '#007bff' }}
                        >
                            M/A{transaction_id}
                        </a>
                    </span>,
                    "warning"
                );

            } else {
                snackbar.show(message, "warning");
            }

            setSaving(false);
        } catch (e) {
            console.error(e);
            snackbar.show("Error when moving Transaction to Draft", "warning");
            setSaving(false);
        }
    };

    const handleComplete = async () => {
        try {
            setSaving(true);

            const updatedTransaction = {
                ...transaction,
                status: 'pending',
            };

            const response = await handleUpdateTransaction(updatedTransaction);

            if (response?.status === 'success') {
                outsideSnackbar.show("Moved to the pending approval stage", "success");
                setTransaction(prev => ({
                    ...prev,
                    status: 'pending',
                }));
            }
        } catch (e) {
            console.error(e);
            snackbar.show("Error when completing Transaction", "warning");
        } finally {
            setSaving(false);
        }
    };

    const handleApprove = async () => {
        if (!adminUserSession?.isSuperAdmin && !adminUserSession?.inHouseResearch) {
            snackbar.show("You do not have permission to set this transaction live", "error");
            return;
        }

        if (window.confirm(`Are you sure you want to set this record live?`)) {
            try {
                setSaving(true);

                const updatedTransaction = {
                    ...transaction,
                    status: 'approved',
                };

                const response = await handleUpdateTransaction(updatedTransaction);
                if (response?.status === 'success') {
                    outsideSnackbar.show("Record has been set live on production.", "success");

                    setTransaction(prev => ({
                        ...prev,
                        status: 'approved',
                    }));
                }
            } catch (e) {
                console.error(e);
                snackbar.show("Error when approving Transaction", "warning");
            } finally {
                setSaving(false);
            }
        }
    };

    // Full date picker / typing
    const handleFullDateChange = (value) => {
        let dateValue = null;

        // Handle null when Clear is clicked
        if (value === null) {
            dateValue = null;
        } else if (value instanceof Date && !isNaN(value)) {
            dateValue = toUTCNoon(value.getFullYear(), value.getMonth(), value.getDate());
        } else if (typeof value === "string" && value.trim() !== "") {
            const parts = value.split("/");
            if (parts.length === 3) {
                const [month, day, year] = parts;
                dateValue = toUTCNoon(Number(year), Number(month) - 1, Number(day));
            } else {
                const parsed = new Date(value);
                if (!isNaN(parsed)) {
                    dateValue = toUTCNoon(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
                }
            }
        }

        setTransaction(prev => ({
            ...prev,
            manual_transaction_date: dateValue,
        }));

        handleOnChangeInField("manual_transaction_date", dateValue);
        if (itemId) setTransactionDateEdited(true);
    };



    // Month/Year picker / typing
    const handleMonthYearChange = (value) => {
        let dateValue = null;

        // Handle null when Clear is clicked
        if (value === null) {
            dateValue = null;
        } else if (value instanceof Date && !isNaN(value)) {
            dateValue = toUTCNoon(value.getFullYear(), value.getMonth(), 1);
        } else if (typeof value === "string" && value.trim() !== "") {
            const [month, year] = value.split("/");
            if (month && year) {
                dateValue = toUTCNoon(Number(year), Number(month) - 1, 1);
            }
        }

        setTransaction(prev => ({
            ...prev,
            manual_transaction_date: dateValue,
        }));

        handleOnChangeInField("manual_transaction_date", dateValue);
        if (itemId) setTransactionDateEdited(true);
    };

    const handleInvestorChange = (type, firm) => {
    let field = ''
    let field_name = ''
    switch (type) {
      case 'acquired':
        field = 'acquired_firm_crd_id'
        field_name = 'acquired_firm_name'
        break;
      case 'acquiring':
        field = 'acquiring_firm_crd_id'
        field_name = 'acquiring_firm_name'
        break;
    }

    clearHistoricalFirmData(type);
    handleOnChangeInField(field, firm ? firm.id : undefined)
    handleOnChangeInField(field_name, firm ? firm.name : null)
  }

  const handleOpenWealthTeam = (wealthTeamData) => {
    // Pass an object with just the id, which is what PrivateWealthTeamForm expects
    setSelectedWealthTeamId({ id: wealthTeamData.id });
    setOpenWealthTeamModal(true);
  };

  const handleCloseWealthTeamModal = () => {
    setOpenWealthTeamModal(false);
    setSelectedWealthTeamId(null);
  };

  const handleOpenFamilyOffice = (familyOfficeData) => {
    // Pass an object with the family office id
    setSelectedFamilyOfficeId({ id: familyOfficeData.id });
    setOpenFamilyOfficeModal(true);
  };

  const handleCloseFamilyOfficeModal = () => {
    setOpenFamilyOfficeModal(false);
    setSelectedFamilyOfficeId(null);
  };

  const handleConfirmNoDate = () => {
    setOpenConfirmNoDateDialog(false);

    // Proceed with save without date
    if (itemId !== undefined) {
      handleUpdateTransaction(transaction);
    } else {
      handleCreateTransaction(transaction);
    }

    setPendingSubmitEvent(null);
  };

  const handleCancelNoDate = () => {
    setOpenConfirmNoDateDialog(false);
    setPendingSubmitEvent(null);
  };

  const onOpenNotes = () => adminNotes.setOpen(true);

  const onRegenerateAiBio = async () => {
    await MATransactionService.regenerateAiBio(itemId, adminUserSession).then((response) => {
      const { status, message } = response.data

      if (status === 'ok') {
        alert(message)
      } else {
        snackbar.show(message);
        console.log('message', message)
      }
    }).catch(function (thrown) {
      console.log('Request canceled', thrown.message);
    });
  }

    return (
        <>
            <Modal
                open={open}
                onClose={onClose}
                style={modalZIndex ? { zIndex: modalZIndex } : undefined}
            >
                <Card className={classes.root}>
                    <CardHeader
                        className="modal-header"
                        title={
                            <div style={{display: 'flex', gap: '12px', alignItems: 'center', minWidth: 0}}>
                    <span> {itemId
                        ? `${transaction?.acquired_firm_name} CRD ${transaction?.acquired_firm_crd_id} --> 
                     ${transaction?.acquiring_firm_crd_id ? `${transaction?.acquiring_firm_name} CRD ${transaction?.acquiring_firm_crd_id}` : "Unidentified"}`
                        : 'New Transaction'}</span>
                                {itemId && <ChipLabel
                                    className={`${classes.statusChip} ${classes[transaction?.status]}`}
                                    label={toTitleCase(mapStage(transaction?.status).replace("_", " "))}
                                /> }
                                {itemId && !transaction?.transaction_date && (
                                    <ChipLabel
                                        className={`${classes.statusChip} ${classes.warning}`}
                                        label="Acquisition in Progress"
                                    />
                                )}
                                {itemId && (
                                    <>
                                        <RegenerateBio
                                            show={adminUserSession.isSuperAdmin || adminUserSession.inHouseResearch}
                                            onRegenerate={onRegenerateAiBio}
                                        />
                                        <Badge
                                            badgeContent={notesQuantity > 0 ? notesQuantity : null}
                                            color="error"
                                            overlap="rectangular"
                                            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                                        >
                                            <Button
                                                color="primary"
                                                variant="contained"
                                                onClick={onOpenNotes}
                                            >
                                                Notes
                                            </Button>
                                        </Badge>
                                    </>
                                )}
                            </div>

                        }
                        action={
                            <div>
                                <span> {itemId && transaction?.transaction_id}</span>
                            </div>
                        }
                    />
                    <Divider />
                    <CardContent style={{height: 'calc(100% - 190px)', overflowY: "auto"}}>
                        <Card>
                            <CardContent >
                                <Grid container spacing={3} style={{height: '100%'}}>
                                    <Grid item xs={6}>
                                        <FormControl fullWidth>
                                            <SingleAsyncAutoComplete
                                                fetch={investors}
                                                handleOnChange={(value) => value ? handleInvestorChange("acquired", value) : null}
                                                value={transaction?.acquired_firm_crd_id ?
                                                    {id: transaction?.acquired_firm_crd_id, name: transaction?.acquired_firm_name} : null}
                                                label="Acquired Firm"
                                                disabled={!!itemId}
                                            />
                                            {!transaction?.acquired_firm_crd_id && !itemId && (
                                                <div className={classes.requiredFieldWarning}>
                                                    * Acquired Firm is required to save
                                                </div>
                                            )}
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <FormControl fullWidth>
                                            <SingleAsyncAutoComplete
                                                fetch={investors}
                                                handleOnChange={(value) => value ? handleInvestorChange("acquiring", value) : null}
                                                getOptionSelected={(option, value) => option.id === value.id}
                                                value={transaction?.acquiring_firm_crd_id ?
                                                    {id: transaction?.acquiring_firm_crd_id, name: transaction?.acquiring_firm_name} : null}
                                                label="Acquiring Firm"
                                                disabled={transaction?.status === 'rejected'}
                                            />
                                            {!transaction?.acquiring_firm_crd_id && (
                                                <div className={classes.requiredFieldWarning}>
                                                    * Acquiring Firm is required to save
                                                </div>
                                            )}
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <FormControl fullWidth>
                                            <TextField
                                                fullWidth
                                                name='acquired_firm_employee_count'
                                                label={"Acquired firm Employee Count"}
                                                onChange={(event) => handleOnChangeInField('acquired_firm_employee_count', event.target.value)}
                                                value={transaction?.acquired_firm_employee_count}
                                                variant='outlined'
                                                type={"number"}
                                                disabled={transaction?.status === 'rejected' ||
                                                  (transaction?.status === "approved" &&
                                                    !(adminUserSession?.isSuperAdmin || adminUserSession?.inHouseResearch))}
                                            />
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <FormControl fullWidth>
                                            <TextField
                                                fullWidth
                                                name='acquiring_firm_employee_count'
                                                label={"Acquiring firm Employee Count"}
                                                onChange={(event) => handleOnChangeInField('acquiring_firm_employee_count', event.target.value)}
                                                value={transaction?.acquiring_firm_employee_count}
                                                variant='outlined'
                                                type={"number"}
                                                disabled={transaction?.status === 'rejected' ||
                                                    (transaction?.status === "approved" &&
                                                      !(adminUserSession?.isSuperAdmin || adminUserSession?.inHouseResearch))}
                                            />
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <FormControl fullWidth>
                                            <TextField
                                                fullWidth
                                                name='acquired_firm_account_count'
                                                label={"Acquired firm Account Count"}
                                                onChange={(event) => handleOnChangeInField('acquired_firm_account_count', event.target.value)}
                                                value={transaction?.acquired_firm_account_count}
                                                variant='outlined'
                                                type={"number"}
                                                disabled={transaction?.status === 'rejected' ||
                                                    (transaction?.status === "approved" &&
                                                      !(adminUserSession?.isSuperAdmin || adminUserSession?.inHouseResearch))}
                                            />
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <FormControl fullWidth>
                                            <TextField
                                                fullWidth
                                                name='acquiring_firm_account_count'
                                                label={"Acquiring firm Account Count"}
                                                onChange={(event) => handleOnChangeInField('acquiring_firm_account_count', event.target.value)}
                                                value={transaction?.acquiring_firm_account_count}
                                                variant='outlined'
                                                type={"number"}
                                                disabled={transaction?.status === 'rejected' ||
                                                    (transaction?.status === "approved" &&
                                                      !(adminUserSession?.isSuperAdmin || adminUserSession?.inHouseResearch))}
                                            />
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <FormControl fullWidth>
                                            <TextField
                                                label="Acquired AUM"
                                                variant="outlined"
                                                name='manual_last_filed_aum_acquired'
                                                value={transaction?.manual_last_filed_aum_acquired || ''}
                                                onBlur={(e) => handleOnChange(e)}
                                                InputProps={{
                                                    inputComponent: CurrencyFormatter
                                                }}
                                                error={!!aumErrors.manual_last_filed_aum_acquired}
                                                helperText={aumErrors.manual_last_filed_aum_acquired}
                                                disabled={transaction?.status === 'rejected' ||
                                                    (transaction?.status === "approved" &&
                                                      !(adminUserSession?.isSuperAdmin || adminUserSession?.inHouseResearch))}
                                            />
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <FormControl fullWidth>
                                            <TextField
                                                label="Acquiring AUM"
                                                variant="outlined"
                                                name='last_filed_aum_acquiring'
                                                value={transaction?.last_filed_aum_acquiring || ''}
                                                onBlur={(e) => handleOnChange(e)}
                                                InputProps={{
                                                    inputComponent: CurrencyFormatter
                                                }}
                                                error={!!aumErrors.last_filed_aum_acquiring}
                                                helperText={aumErrors.last_filed_aum_acquiring}
                                                disabled={transaction?.status === 'rejected' ||
                                                    (transaction?.status === "approved" &&
                                                      !(adminUserSession?.isSuperAdmin || adminUserSession?.inHouseResearch))}
                                            />
                                        </FormControl>
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Grid container spacing={2}>
                                            <Grid item xs={4} >
                                                <FormControl fullWidth>
                                                    <Box mb={1} display="flex" alignItems="center" justifyContent="space-between" minHeight="42px">
                                                        <FormControlLabel
                                                            control={
                                                                <Checkbox
                                                                    checked={!isFullDate}
                                                                    onChange={handleModeToggle}
                                                                    disabled={transaction?.status === "approved" && !canApproveLive}
                                                                    color="primary"
                                                                />
                                                            }
                                                            label="Only month and year available"
                                                        />
                                                    </Box>

                                                    <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                        {isFullDate ? (
                                                            <KeyboardDatePicker
                                                                disableFuture
                                                                inputVariant="outlined"
                                                                label="Date of Transaction"
                                                                format="MM/dd/yyyy"
                                                                value={transaction?.manual_transaction_date || null}
                                                                onChange={(value) => handleFullDateChange(value)}
                                                                inputProps={{ placeholder: "MM/DD/YYYY" }}
                                                                maxDate={new Date()}
                                                                clearable
                                                                clearLabel="Clear"
                                                                disabled={transaction?.status === 'rejected' ||
                                                                    (transaction?.status === "approved" &&
                                                                      !(adminUserSession?.isSuperAdmin || adminUserSession?.inHouseResearch))}
                                                                fullWidth
                                                            />
                                                        ) : (
                                                            <KeyboardDatePicker
                                                                disableFuture
                                                                inputVariant="outlined"
                                                                label="Month and Year of Transaction"
                                                                views={['year', 'month']}
                                                                openTo="month"
                                                                format="MM/yyyy"
                                                                maxDate={new Date()}
                                                                value={transaction?.manual_transaction_date || null}
                                                                onChange={(value) => handleMonthYearChange(value)}
                                                                inputProps={{ placeholder: "MM/YYYY" }}
                                                                clearable
                                                                clearLabel="Clear"
                                                                disabled={transaction?.status === 'rejected' ||
                                                                    (transaction?.status === "approved" &&
                                                                      !(adminUserSession?.isSuperAdmin || adminUserSession?.inHouseResearch))}
                                                                fullWidth
                                                            />
                                                        )}
                                                    </MuiPickersUtilsProvider>
                                                </FormControl>
                                            </Grid>

                                            {itemId && transaction?.calculated_date != null && (
                                                <Grid item xs={4}>
                                                    <FormControl fullWidth>
                                                        <Box mb={1} display="flex" alignItems="center" minHeight="42px">
                                                            {transaction?.date_source && (
                                                                <MaSourceChips source={transaction?.date_source}></MaSourceChips>
                                                            )}
                                                        </Box>

                                                        <TextField
                                                            fullWidth
                                                            label="Date Shown on Platform"
                                                            variant="outlined"
                                                            value={transaction.calculated_date ? (
                                                              dateFormat(transaction.calculated_date)
                                                            ) : ''}
                                                            disabled
                                                            InputProps={{
                                                                style: { backgroundColor: '#f5f5f5' }
                                                            }}
                                                        />
                                                    </FormControl>
                                                </Grid>
                                            )}

                                            {itemId && transaction?.calculated_aum != null && (
                                                <Grid item xs={4}>
                                                    <FormControl fullWidth>
                                                        <Box mb={1} display="flex" alignItems="center" minHeight="42px">
                                                            {transaction?.aum_source && (
                                                                <MaSourceChips source={transaction?.aum_source}/>
                                                            )}
                                                        </Box>

                                                        <TextField
                                                            fullWidth
                                                            label="Acquired AUM Shown on Platform"
                                                            variant="outlined"
                                                            value={`$${Number(transaction.calculated_aum).toLocaleString('en-US')}`}
                                                            disabled
                                                            InputProps={{
                                                                style: { backgroundColor: '#f5f5f5' }
                                                            }}
                                                        />
                                                    </FormControl>
                                                </Grid>
                                            )}
                                        </Grid>
                                    </Grid>

                                    <Grid item xs={12}>
                                        <FormControl fullWidth>
                                            <TextField
                                                fullWidth
                                                label="Transaction Information"
                                                variant="outlined"
                                                name={'transaction_information'}
                                                onChange={(e) => {
                                                    handleOnChange(e);
                                                }}
                                                value={transaction?.transaction_information || ''}
                                                multiline={true}
                                                maxRows={16}
                                                minRows={8}
                                                disabled={transaction?.status === 'rejected' ||
                                                    (transaction?.status === "approved" &&
                                                        !(adminUserSession?.isSuperAdmin || adminUserSession?.inHouseResearch))}
                                            />
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <FormControl fullWidth>
                                            <TextField
                                                fullWidth
                                                name='adv_w_link'
                                                label={"ADV W Link"}
                                                onChange={(event) => handleOnChangeInField('adv_w_link', event.target.value)}
                                                value={transaction?.adv_w_link}
                                                variant='outlined'
                                                disabled={transaction?.status === 'rejected' ||
                                                    (transaction?.status === "approved" &&
                                                        !(adminUserSession?.isSuperAdmin || adminUserSession?.inHouseResearch))}
                                            />
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Grid item xs={12}>
                                            {optionsData.bankers_used &&
                                                <FilterAutoComplete
                                                    options={optionsData.bankers_used}
                                                    handleOnChange={(value) => handleBankersUsedChange(value)}
                                                    selected={selectedBankersUsed}
                                                    label={'Bankers Used'}
                                                    field={'bankers_used'}
                                                    multiple
                                                    disabled={transaction?.status === 'rejected' ||
                                                        (transaction?.status === "approved" &&
                                                            !(adminUserSession?.isSuperAdmin || adminUserSession?.inHouseResearch))}
                                                />
                                            }
                                        </Grid>
                                    </Grid>
                                    <Grid item xs={12}>
                                        {optionsData.service_providers_used &&
                                            <FilterAutoComplete
                                                options={optionsData.service_providers_used}
                                                handleOnChange={(value) => handleServiceProvidersUsedChange(value)}
                                                selected={selectedServiceProvidersUsed}
                                                label={'Service Providers Used'}
                                                field={'service_providers_used'}
                                                multiple
                                                disabled={transaction?.status === 'rejected' ||
                                                    (transaction?.status === "approved" &&
                                                        !(adminUserSession?.isSuperAdmin || adminUserSession?.inHouseResearch))}
                                            />
                                        }
                                    </Grid>
                                </Grid>
                                <Divider />
                            </CardContent>
                            <Divider />
                        </Card>

                        {/* Corresponding Wealth Team Section */}
                        {itemId && transaction?.corresponding_wealth_team?.team_name && (
                            <Card style={{ marginTop: '20px' }}>
                                <CardHeader
                                    title="Corresponding Wealth Team"
                                    style={{ paddingBottom: '8px' }}
                                />
                                <Divider />
                                <CardContent>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <span style={{ fontWeight: 500, color: '#666' }}>Team Name:</span>
                                        <Link
                                            onClick={() => handleOpenWealthTeam(transaction.corresponding_wealth_team)}
                                        >
                                            {transaction.corresponding_wealth_team.team_name}
                                        </Link>
                                    </div>
                                </CardContent>
                            </Card>
                        )}

                        {/* Corresponding Family Office Section */}
                        {itemId && transaction?.corresponding_family_office && (
                            <Card style={{ marginTop: '20px' }}>
                                <CardHeader
                                    title="Corresponding Family Office"
                                    style={{ paddingBottom: '8px' }}
                                />
                                <Divider />
                                <CardContent>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <span style={{ fontWeight: 500, color: '#666' }}>Family Office:</span>
                                        <Link
                                            onClick={() => handleOpenFamilyOffice(transaction.corresponding_family_office)}
                                        >
                                            {transaction.corresponding_family_office.name}
                                        </Link>
                                    </div>
                                </CardContent>
                            </Card>
                        )}

                        {itemId &&
                            <MATransactionNews
                                news={news}
                                fetchNews={fetchNews}
                                newsTypes={newsTypes}
                                riaInvestorParents={riaInvestorParent}
                                fetching={fetchingNews}
                                transaction={transaction}
                                canEdit={!(transaction?.status === 'rejected' || transaction?.status === "approved" && !canApproveLive)}
                            />
                        }
                        {
                            (adminUserSession.isSuperAdmin || adminUserSession.inHouseResearch) &&
                            itemId && transaction?.source.length > 0  &&
                            <TriggeringContactsSection contacts={contacts} fetching={fetchingContacts} open={!!open} handleOpenContactModal={handleOpenContactModal}/>
                        }
                    </CardContent>
                    <Divider />
                    <CardActions>
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                width: "100%",
                            }}
                        >
                            {/* LEFT BUTTON GROUP */}
                            <div style={{ display: "flex", gap: "8px" }}>
                                <Button onClick={onClose}>Close</Button>

                                {transaction?.status === "rejected" && (
                                    <Button
                                        className={classes.draft}
                                        variant="contained"
                                        onClick={handleMoveToDraft}
                                    >
                                        Move to Draft
                                    </Button>
                                )}

                                {transaction?.status !== "rejected" && (
                                    <Button
                                        color="primary"
                                        variant="contained"
                                        onClick={handleSubmit}
                                        disabled={
                                            saving ||
                                            !isDirty ||
                                            !transaction?.acquiring_firm_crd_id ||
                                            !transaction?.acquired_firm_crd_id ||
                                            (transaction?.status === "approved" && !canApproveLive)
                                        }
                                    >
                                        Save
                                    </Button>
                                )}

                                {itemId && (
                                    <>
                                      {transaction?.status === "pending" && (
                                        <Button
                                          className={"status-chip success"}
                                          variant="contained"
                                          onClick={handleApprove}
                                          disabled={
                                              !canApproveLive ||
                                              saving ||
                                              isDirty ||
                                              !transaction?.acquiring_firm_crd_id ||
                                              !transaction?.acquired_firm_crd_id ||
                                              !transaction?.acquiring_firm_name ||
                                              !transaction?.acquired_firm_name
                                          }
                                        >
                                          {saving ? (
                                            <CircularProgress style={{ height: "25px", width: "25px" }} />
                                          ) : (
                                            "Set Live"
                                          )}
                                        </Button>
                                      )}

                                      {transaction?.status === "draft" && (
                                            <Button
                                                className={"status-chip success"}
                                                variant="contained"
                                                onClick={handleComplete}
                                                disabled={
                                                    saving ||
                                                    isDirty ||
                                                    !transaction?.acquiring_firm_crd_id ||
                                                    !transaction?.acquired_firm_crd_id ||
                                                    !transaction?.acquiring_firm_name ||
                                                    !transaction?.acquired_firm_name
                                                }
                                            >
                                                {saving ? (
                                                    <CircularProgress style={{ height: "25px", width: "25px" }} />
                                                ) : (
                                                    "Complete"
                                                )}
                                            </Button>
                                        )}
                                    </>
                                )}
                            </div>

                            {/* RIGHT TRASH BUTTON */}
                            {itemId && transaction?.status !== "rejected" && (
                                <IconButton aria-label="delete" onClick={handleReject} disabled={saving || (transaction?.status === "approved" && !canApproveLive)}
                                            style={{padding: "6px"}}>
                                    {saving ? (
                                        <CircularProgress style={{ height: "25px", width: "25px" }} />
                                    ) : (
                                        <DeleteOutlined />
                                    )}
                                </IconButton>
                            )}
                        </div>
                    </CardActions>


                    {
                        snackbar.open ?
                            <CustomSnackbar
                                open={snackbar.open}
                                closeHandler={handleCloseSnackBar}
                                message={snackbar.message}
                                options={{
                                    severity: 'success'
                                }}
                            />
                            : null
                    }

                    {openRiaContactModal ? <AdminEditContactContainer
                        contactId={selectedContactId}
                        onClose={handleCloseContactModal}
                        open={openRiaContactModal}
                        onDataUpdate={onDataUpdate}
                        fromInvestorModal={false}
                    /> : ''}
                </Card>
            </Modal>
            <NotesDialog
                label={"Add a Note"}
                body={""}
                adminNotes={adminNotes}
            />
            {openWealthTeamModal && selectedWealthTeamId && (
                <React.Suspense fallback={<CircularProgress />}>
                    <PrivateWealthTeamForm
                        session={adminUserSession}
                        team={selectedWealthTeamId}
                        onClose={handleCloseWealthTeamModal}
                        open={openWealthTeamModal}
                        afterCreate={() => {}}
                        onDataUpdate={() => {}}
                        snackbar={snackbar}
                    />
                </React.Suspense>
            )}
            {openFamilyOfficeModal && selectedFamilyOfficeId && (
                <Modal
                    onClose={handleCloseFamilyOfficeModal}
                    open={openFamilyOfficeModal}
                    style={{ zIndex: 1400 }}
                >
                    <div style={{
                        position: 'absolute',
                        top: '5%',
                        left: '5%',
                        outline: 'none',
                        boxShadow: '0px 11px 15px -7px rgba(0,0,0,0.2), 0px 24px 38px 3px rgba(0,0,0,0.14), 0px 9px 46px 8px rgba(0,0,0,0.12)',
                        height: '90%',
                        overflowY: 'auto',
                        width: '90%',
                        backgroundColor: '#fff'
                    }}>
                        <React.Suspense fallback={<CircularProgress />}>
                            <InvestorForm
                                adminUserSession={adminUserSession}
                                investorId={selectedFamilyOfficeId.id}
                                onClose={handleCloseFamilyOfficeModal}
                                open={openFamilyOfficeModal}
                                afterCreate={() => {}}
                                afterUpdate={() => {}}
                                snackbar={snackbar}
                                dataType="fintrx_data"
                                investorExtraData={investorExtraData}
                            />
                        </React.Suspense>
                    </div>
                </Modal>
            )}
            <ConfirmDialog
                open={openConfirmNoDateDialog}
                title="Save Record Without Transaction Date"
                confirmText="Yes"
                cancelText="No"
                onConfirm={handleConfirmNoDate}
                onCancel={handleCancelNoDate}
                confirmColor="primary"
            >
                <p>
                    You are attempting to save a record with no transaction date.
                    This means the acquisition must still be in progress.
                    Would you like to continue?
                </p>
            </ConfirmDialog>
        </>
  )
}

export default MATransactionForm;

